import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { Box, FormControl, IconButton, InputLabel, MenuItem, Select, TextField, Typography } from '@fairstone/ui/core';
import { AddressAutoComplete } from '@fairstone/ui/core/components/AddressAutoComplete';
import { t } from '@fairstone/ui/core/providers/Intl/locales/translate';
import { AddressRetrieveType } from '@fairstone/ui/core/types/canadaPost';
import useAuth from 'context/useAuth';

import { ReactComponent as EditIcon } from 'assets/icons/edit-icon.svg';
import ErrorView from 'components/errors/ErrorView';
import EditProfileWrapper from 'components/forms/profile/EditProfileWrapper';
import TextMaskCustom from 'components/inputs/TextMaskCustom';
import CustomModal from 'components/modals/CustomModal';
import { CurrentAddress } from 'components/profile/CurrentAddress/CurrentAddress';
import OverlaySpinner from 'components/spinner/OverlaySpinner';
import { PrimaryButtonSto, TextButtonSto } from 'components/sto';
import { AppRoutes } from 'routes';
import { updateProfile } from 'services/AccountService';
import { CustomModalType } from 'types';

import styles from './ProfileAddressPage.module.scss';

type FieldsToUpdateType = {
    city: string;
    postalCode: string;
    province: string;
    unit: string;
    streetName?: string;
    streetNumber?: string;
    ruralRoute?: string;
    poBox?: string;
};

const initialModal: CustomModalType = {
    modalType: '',
    okText: '',
    title: '',
    visible: false,
};

const emptySelectedAddress: AddressRetrieveType = {
    BuildingNumber: '',
    City: '',
    Id: '',
    Line1: '',
    POBoxNumber: '',
    PostalCode: '',
    ProvinceCode: 'AB',
    Street: '',
    SubBuilding: '',
};

const provinces = ['AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT'];

const getStreetNameError = (selectedAddress: AddressRetrieveType): string => {
    if (selectedAddress?.Street == '' || !selectedAddress?.Line1.includes('RR')) {
        return 'pages.profile.edit.error.street.or.ruralRoute';
    } else if (!selectedAddress?.Street || !selectedAddress?.BuildingNumber) {
        return 'pages.profile.edit.error.street.name"';
    }
    return '';
};

const ProfileAddressPage = (): React.ReactElement => {
    const { setUserProfile, userProfile } = useAuth();
    const { formatMessage, locale } = useIntl();
    const history = useHistory();
    const [apiError, setApiError] = useState<string>('');
    const [isAddressSelected, setIsAddressSelected] = useState<boolean>(false);
    const [isEditingAddress, setIsEditingAddress] = useState<boolean>(false);
    const [isFormDisabled, setIsFormDisabled] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [modal, setModal] = useState<CustomModalType>(initialModal);
    const [selectedAddress, setSelectedAddress] = useState<AddressRetrieveType>(emptySelectedAddress);

    const autoCompleteTranslations = {
        noOptions: t('pages.profile.edit.enter.address'),
        searchPlaceholder: t('autocomplete.search-placeholder'),
    };

    const handleApiError = (error: any) => {
        const { status } = error?.response || {};
        if (status === 429) {
            setModal(initialModal);
            setIsFormDisabled(true);
            return setApiError('pages.profile.edit.error.limit.exceeded');
        }
        setModal({
            modalType: 'fail',
            okText: 'general.ok',
            onOk: handleGoBack,
            title: 'pages.profile.edit.failed',
            visible: true,
        });
    };

    const handleApiSuccess = () => {
        setModal({
            modalType: 'success',
            okText: 'general.ok',
            onOk: handleGoBack,
            title: 'pages.profile.edit.address.success.text',
            visible: true,
        });
    };

    const handleGoBack = () => {
        setModal((prevState: CustomModalType) => ({ ...prevState, visible: false }));
        history.push(`/${locale}/${formatMessage({ id: 'tab.label.profile' }).toLowerCase()}`);
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        try {
            e.preventDefault();
            setIsSubmitting(true);
            const fieldsToUpdate: FieldsToUpdateType = {
                city: selectedAddress.City,
                postalCode: selectedAddress.PostalCode,
                province: selectedAddress.ProvinceCode,
                unit: selectedAddress.SubBuilding,
            };
            if (selectedAddress.Street) {
                fieldsToUpdate.streetName = selectedAddress.Street;
                fieldsToUpdate.streetNumber = selectedAddress.BuildingNumber;
            }
            if (selectedAddress.Line1) fieldsToUpdate.ruralRoute = selectedAddress.Line1;
            if (selectedAddress.POBoxNumber) fieldsToUpdate.poBox = selectedAddress.POBoxNumber;

            const res = await updateProfile(fieldsToUpdate);
            setUserProfile((prevState: any) => ({
                ...prevState,
                addressLine1: res.find((o: any) => o.fieldToUpdate === 'addressLine1').fieldData,
                addressLine2: res.find((o: any) => o.fieldToUpdate === 'addressLine2').fieldData,
                city: res.find((o: any) => o.fieldToUpdate === 'city').fieldData,
                postalCode: res.find((o: any) => o.fieldToUpdate === 'postalCode').fieldData,
                state: res.find((o: any) => o.fieldToUpdate === 'province').fieldData,
            }));
            handleApiSuccess();
        } catch (error) {
            handleApiError(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const onChange = (event: any) => {
        setSelectedAddress((prevState: AddressRetrieveType) => ({
            ...prevState,
            [event.target.name]: event.target.value,
        }));
    };

    const onSelectedAddress = (pickedAddress: AddressRetrieveType | null) => {
        if (pickedAddress) {
            setIsAddressSelected(true);
            setSelectedAddress({
                ...pickedAddress,
                Line1: pickedAddress.Line1.includes('RR') ? pickedAddress.Line1 : '',
                POBoxNumber:
                    pickedAddress.POBoxNumber && pickedAddress.Line1.includes('PO Box') ? pickedAddress.Line1 : '',
                Street: pickedAddress.Line1.startsWith('GD') ? pickedAddress.Line1 : pickedAddress.Street,
            });
        }
    };

    const isFormValid = useMemo(() => {
        const requiredAddressFields: string[] = ['City', 'ProvinceCode', 'PostalCode'];
        const isMissingRequiredFields = requiredAddressFields.some(
            (field) => !selectedAddress[field as keyof AddressRetrieveType]
        );
        if (isMissingRequiredFields) return false;

        const hasPhysicalAddress =
            (!!selectedAddress.Street && !!selectedAddress.BuildingNumber) ||
            selectedAddress.Street.toUpperCase().startsWith('GD');
        const hasRuralRoute = !!selectedAddress.Line1;
        if (!hasPhysicalAddress && !hasRuralRoute) return false;

        return true;
    }, [
        selectedAddress.City,
        selectedAddress.ProvinceCode,
        selectedAddress.PostalCode,
        selectedAddress.Street,
        selectedAddress.BuildingNumber,
        selectedAddress.Line1,
    ]);

    if (isSubmitting)
        return (
            <OverlaySpinner>
                <p>{t('spinner.submitting.information')}</p>
                <p>{t('spinner.message.2')}</p>
            </OverlaySpinner>
        );

    return (
        <EditProfileWrapper goBack={AppRoutes.profile} title={'pages.profile.title.address'}>
            <Box className={styles.profileAddressContainer}>
                <div className={styles.profileAddressContent}>
                    {apiError && <ErrorView error={apiError} />}
                    {isEditingAddress ? (
                        <div className={styles.editAddressContainer}>
                            <AddressAutoComplete
                                dictionary={autoCompleteTranslations}
                                onSelectedAddress={onSelectedAddress}
                                postCanadaKey={process.env.REACT_APP_CANADA_POST_API_KEY as string}
                            ></AddressAutoComplete>
                            {isAddressSelected && (
                                <AddressForm
                                    handleSubmit={handleSubmit}
                                    isFormDisabled={isFormDisabled}
                                    isFormValid={!isFormValid}
                                    onChange={onChange}
                                    selectedAddress={selectedAddress}
                                ></AddressForm>
                            )}
                            <TextButtonSto
                                labelKey="general.cancel"
                                onClick={() => {
                                    setIsEditingAddress(false);
                                    setSelectedAddress(emptySelectedAddress);
                                    setIsAddressSelected(false);
                                }}
                                type="button"
                            />
                        </div>
                    ) : (
                        <div>
                            <Typography className={styles.textLabel}>
                                {t('pages.profile.edit.current.address')}
                            </Typography>
                            <div className={styles.profileAddressCurrentContainer}>
                                <CurrentAddress userProfile={userProfile} />
                                <IconButton
                                    className={styles.editIcon}
                                    onClick={() => setIsEditingAddress(true)}
                                    title={formatMessage({ id: 'pages.profile.edit' })}
                                >
                                    <EditIcon />
                                </IconButton>
                            </div>
                        </div>
                    )}
                </div>
                <CustomModal
                    modalType={modal.modalType}
                    okText={modal.okText}
                    onOk={modal.onOk}
                    visible={modal.visible}
                >
                    <p>{t(modal.title)}</p>
                </CustomModal>
            </Box>
        </EditProfileWrapper>
    );
};

interface AddressFormProps {
    handleSubmit: React.FormEventHandler<HTMLFormElement>;
    isFormDisabled: boolean;
    isFormValid: boolean;
    selectedAddress: AddressRetrieveType;
    onChange: any;
}
const AddressForm: React.FC<AddressFormProps> = ({
    handleSubmit,
    isFormDisabled,
    isFormValid,
    onChange,
    selectedAddress,
}) => (
    <form onSubmit={handleSubmit}>
        <fieldset className={styles.textFieldContainer} disabled={isFormDisabled}>
            <TextField
                InputLabelProps={{
                    className: styles.textLabel,
                    shrink: true,
                }}
                InputProps={{
                    className: styles.textInput,
                    inputMode: 'numeric',
                }}
                autoComplete="off"
                className={styles.textField}
                disabled={!!selectedAddress.POBoxNumber && !!selectedAddress.Line1}
                error={
                    !selectedAddress.Line1 &&
                    !!selectedAddress.Street &&
                    !selectedAddress.Street.toUpperCase().startsWith('GD') &&
                    !selectedAddress.BuildingNumber
                }
                fullWidth
                helperText={
                    !selectedAddress.Line1 &&
                    !!selectedAddress.Street &&
                    !selectedAddress.Street.toUpperCase().startsWith('GD') &&
                    !selectedAddress.BuildingNumber && (
                        <span className={styles.errorText}>{t('pages.profile.edit.error.street.number')}</span>
                    )
                }
                label={
                    <Box className={styles.textLabelContainer}>
                        <Typography className={styles.textLabel}>
                            {t('pages.profile.edit.address.street.number')}
                        </Typography>
                    </Box>
                }
                name="BuildingNumber"
                onChange={onChange}
                value={selectedAddress.BuildingNumber}
                variant="standard"
            />
            <TextField
                InputLabelProps={{ className: styles.textLabel, shrink: true }}
                InputProps={{
                    className: styles.textInput,
                }}
                autoComplete="off"
                className={styles.textField}
                disabled={!!selectedAddress.POBoxNumber && !!selectedAddress.Line1}
                error={selectedAddress.Street == '' && !selectedAddress.Line1}
                fullWidth
                helperText={
                    selectedAddress.Street == '' &&
                    !selectedAddress.Line1 && (
                        <span className={styles.errorText}>{t(getStreetNameError(selectedAddress))}</span>
                    )
                }
                label={
                    <Box className={styles.textLabelContainer}>
                        <Typography className={styles.textLabel}>
                            {t('pages.profile.edit.address.street.name')}
                        </Typography>
                    </Box>
                }
                name="Street"
                onChange={onChange}
                value={selectedAddress.Street}
                variant="standard"
            />
            <TextField
                InputLabelProps={{
                    className: styles.textLabel,
                    shrink: true,
                }}
                InputProps={{
                    className: styles.textInput,
                    inputMode: 'numeric',
                }}
                autoComplete="off"
                className={styles.textField}
                disabled={!!selectedAddress.POBoxNumber && !!selectedAddress.Line1}
                fullWidth
                label={
                    <Box className={styles.textLabelContainer}>
                        <Typography className={styles.textLabel}>
                            {t('pages.profile.edit.address.unit.number')}
                        </Typography>
                    </Box>
                }
                name="SubBuilding"
                onChange={onChange}
                value={selectedAddress.SubBuilding}
                variant="standard"
            />
            <TextField
                InputLabelProps={{ className: styles.textLabel, shrink: true }}
                InputProps={{
                    className: styles.textInput,
                }}
                autoComplete="off"
                className={styles.textField}
                error={!selectedAddress.City}
                fullWidth
                helperText={
                    !selectedAddress.City && (
                        <span className={styles.errorText}>{t('pages.profile.edit.error.city')}</span>
                    )
                }
                label={
                    <Box className={styles.textLabelContainer}>
                        <Typography className={styles.textLabel}>{t('pages.profile.edit.address.city')}</Typography>
                    </Box>
                }
                name="City"
                onChange={onChange}
                value={selectedAddress.City}
                variant="standard"
            />
            <div className={styles.addressSubContainer}>
                <FormControl className={styles.dropDownContainer} variant="standard">
                    <InputLabel className={styles.textLabel} id="province">
                        {t('pages.profile.edit.address.province')}
                    </InputLabel>
                    <Select
                        className={styles.dropDownInput}
                        fullWidth
                        id="province-select"
                        labelId="province"
                        name="ProvinceCode"
                        onChange={onChange}
                        value={selectedAddress.ProvinceCode}
                    >
                        {provinces.map((province) => (
                            <MenuItem key={province} value={province}>
                                {province}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    InputLabelProps={{ className: styles.textLabel, shrink: true }}
                    InputProps={{
                        className: styles.textInput,
                        inputComponent: TextMaskCustom,
                    }}
                    autoComplete="off"
                    className={styles.textField}
                    error={!selectedAddress.PostalCode}
                    fullWidth
                    helperText={
                        !selectedAddress.PostalCode && (
                            <span className={styles.errorText}>{t('pages.profile.edit.error.postalCode')}</span>
                        )
                    }
                    inputProps={{
                        definitions: {
                            '#': /\d/,
                            '@': /[a-zA-Z]/,
                        },
                        mask: '@#@ #@#',
                    }}
                    label={
                        <Box className={styles.textLabelContainer}>
                            <Typography className={styles.textLabel}>
                                {t('pages.profile.edit.address.postalCode')}
                            </Typography>
                        </Box>
                    }
                    name="PostalCode"
                    onChange={onChange}
                    value={selectedAddress.PostalCode}
                    variant="standard"
                />
            </div>
            <TextField
                InputLabelProps={{ className: styles.textLabel, shrink: true }}
                InputProps={{
                    className: styles.textInput,
                }}
                autoComplete="off"
                className={styles.textField}
                disabled={!!selectedAddress.Line1 && !!selectedAddress.Street}
                fullWidth
                label={
                    <Box className={styles.textLabelContainer}>
                        <Typography className={styles.textLabel}>{t('pages.profile.edit.address.poBox')}</Typography>
                    </Box>
                }
                name="POBoxNumber"
                onChange={onChange}
                value={selectedAddress.POBoxNumber}
                variant="standard"
            />
            <TextField
                InputLabelProps={{ className: styles.textLabel, shrink: true }}
                InputProps={{
                    className: styles.textInput,
                }}
                autoComplete="off"
                className={styles.textField}
                disabled={!!selectedAddress.POBoxNumber && !!selectedAddress.Street}
                error={!selectedAddress.Line1 && selectedAddress?.Street == ''}
                fullWidth
                helperText={
                    !selectedAddress.Line1 &&
                    selectedAddress.Street == '' && (
                        <span className={styles.errorText}>{t('pages.profile.edit.error.street.or.ruralRoute')}</span>
                    )
                }
                label={
                    <Box className={styles.textLabelContainer}>
                        <Typography className={styles.textLabel}>
                            {t('pages.profile.edit.address.ruralRoute')}
                        </Typography>
                    </Box>
                }
                name="Line1"
                onChange={onChange}
                value={selectedAddress.Line1}
                variant="standard"
            />

            <div className={styles.buttonContainer}>
                <PrimaryButtonSto
                    aria-disabled={isFormValid}
                    disabled={isFormValid}
                    labelKey="general.save"
                    type="submit"
                />
            </div>
        </fieldset>
    </form>
);

export default ProfileAddressPage;
